import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { isAdmin, hasAnyRole, canViewServiceProviders, UserRole } from '@/utils/roleHelpers';
import { env } from '@/config/env';

interface AdminOrSpStaffRouteProps {
  children: React.ReactElement;
}

/**
 * Protected route wrapper for pages accessible by Admin or SpStaff users
 * Redirects other users to dashboard
 */
const AdminOrSpStaffRoute: React.FC<AdminOrSpStaffRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  // Bypass guard when authentication is disabled (development mode)
  if (!env.authEnabled && !env.authorizationEnabled) {
    return children;
  }

  // If not authenticated, redirect to login (handled by AuthGuard)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated but not admin, SpAdmin, or SpStaff, redirect to dashboard.
  // Admins who can already reach Service Provider Users via the SP detail
  // page (canViewServiceProviders) are excluded here too, mirroring
  // AppSidebar's nav filter — otherwise this page would still be reachable
  // by direct URL even after the nav item is hidden.
  const isAdminUser = user && isAdmin(user.roles) && !canViewServiceProviders(user.roles, user.organizationName ?? '');
  const isSpUser = user && hasAnyRole(user.roles, [UserRole.SpAdmin, UserRole.SpStaff]);

  if (!isAdminUser && !isSpUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default AdminOrSpStaffRoute;
